// @ts-check
import React, { StatelessComponent } from 'react'; // eslint-disable-line no-unused-vars
import { StyledLabelAndErrorComponent, StyledErrorComponent } from './index'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Field } from 'utils/reduxFormWrapper';

// Wraps a form component with some DOM with bootstrap classes for label, errors, etc.
/** @type { StyledLabelAndErrorComponent } */
const StyledLabelAndError = ({ inputId, label, children, meta, ...rest }) => {
  const showError = meta && meta.touched && meta.error;
  const controlGroupClasses = cx('control-group', 'new-booking-form-group', { error: showError });

  return (
    <div id={`${inputId}-wrapper`} className={controlGroupClasses} {...rest}>
      <label htmlFor={inputId} className="control-label">
        {label}
      </label>
      <div className="controls">
        {children}
        {showError ? <span className="help-inline">{meta.error}</span> : null}
      </div>
    </div>
  );
};

StyledLabelAndError.displayName = 'StyledLabelAndError';

StyledLabelAndError.propTypes = {
  inputId: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node.isRequired,
};

// Wraps a form component with some DOM That displays validation errors if present
/** @type { StyledErrorComponent } */
const StyledError = ({ meta, children }) => {
  const showError = meta && meta.touched && meta.error;
  return (
    <div>
      <div>{children}</div>
      {showError ? <div style={{ color: 'rgb(244, 67, 54)' }}>{meta.error}</div> : null}
    </div>
  );
};

const ConnectedLabelAndError = ({ fieldName, ...props }) => (
  <Field
    name={fieldName}
    render={reduxFormProps => (
      <StyledLabelAndError inputId={fieldName} {...reduxFormProps} {...props} />
    )}
  />
);

ConnectedLabelAndError.displayName = 'ConnectedLabelAndError';

/*
This is a HOC that takes a form component and returns a component
that intercepts and displays field errors and labels

Notes:
  1. If you want to pass props to the styled label and error wrapper, pass them
     in a `wrapperProps` prop.
  2. If you pass children to this component, they will be rendered within
     the label and error wrapper immediately after the form component
  3. All other props are passed to the underlying form component
*/
function wrapFormComponentWithLabelAndError(FormComponent) {
  return ({ name, label, meta, wrapperProps, children, ...inputProps }) => (
    <StyledLabelAndError inputId={name} label={label} meta={meta} {...wrapperProps}>
      <FormComponent placeholder={label} name={name} {...inputProps} />
      {children}
    </StyledLabelAndError>
  );
}

/*
This is a HOC that takes a form component and returns a component
that intercepts and displays field errors

Notes:
  1. the meta prop is passed on to the raw styled input wrapper
  2. all other props are passed to the underlying form component
*/
function wrapFormComponentWithError(FormComponent) {
  return ({ meta, ...rest }) => (
    <StyledError meta={meta}>
      <FormComponent {...rest} />
    </StyledError>
  );
}

export { wrapFormComponentWithLabelAndError, wrapFormComponentWithError, StyledLabelAndError };
export default ConnectedLabelAndError;
